import React, { FC, useMemo } from "react";
import { TableHead, TableBody, IconButton, Typography, Tooltip, Checkbox } from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { DirectBillingRxDto } from "../../../../../../api-client";
import { ValidationMap } from "../types/interface";
import RubberStamp from "../../common/components/RubberStamp";
import { SummarizedValidityIndicator } from "../../../../Base/components/SummarizedValidityIndicator";
import {
  DenseTableContainer,
  DenseTable,
  HeaderCell,
  DataCell,
  DenseTableRow,
  RxDetailsRow,
  TherapyTag,
} from "../styles/primitives";
import { table } from "../styles/tokens";

export interface GkvBillTableProps {
  rxs: DirectBillingRxDto[];
  validationMap: ValidationMap;
  selectedRxId: string | null;
  selectedRxIds: Set<string>;
  onRowClick: (rx: DirectBillingRxDto) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, rx: DirectBillingRxDto) => void;
  onToggleRxSelection: (rxId: string) => void;
}

/**
 * Table displaying Rx rows within a payer card.
 *
 * - Sorts by patient lastName then rxNumber.
 * - Shows validation indicator per Rx from the shared `validationMap`.
 * - Highlights the currently selected row.
 */
const GkvBillTable: FC<GkvBillTableProps> = ({
  rxs,
  validationMap,
  selectedRxId,
  selectedRxIds,
  onRowClick,
  onMenuOpen,
  onToggleRxSelection,
}) => {
  /** Sort Rxs by patient lastName → rxNumber */
  const sortedRxs = useMemo(
    () =>
      [...rxs].sort((a, b) => {
        const lastA = a.patient.lastName.toLowerCase();
        const lastB = b.patient.lastName.toLowerCase();
        if (lastA !== lastB) return lastA.localeCompare(lastB);
        return (a.rxNumber || "").localeCompare(b.rxNumber || "");
      }),
    [rxs],
  );

  return (
    <DenseTableContainer>
      <DenseTable stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <DenseTableRow>
            <HeaderCell padding="checkbox" sx={{ width: "42px" }} />
            <HeaderCell sx={{ width: "22%" }}>Name</HeaderCell>
            <HeaderCell sx={{ width: "35%" }}>Rx Details</HeaderCell>
            <HeaderCell sx={{ width: "15%" }}>Validierung</HeaderCell>
            <HeaderCell sx={{ width: "18%" }}>Taxieren</HeaderCell>
            <HeaderCell sx={{ width: "48px" }} />
          </DenseTableRow>
        </TableHead>
        <TableBody>
          {sortedRxs.map((rx) => {
            const isSelected = rx.id === selectedRxId;

            return (
              <DenseTableRow key={rx.id} isSelected={isSelected} onClick={() => onRowClick(rx)}>
                {/* Checkbox */}
                <DataCell padding="checkbox" sx={{ width: table.checkboxWidth }}>
                  <Checkbox
                    size="small"
                    checked={selectedRxIds.has(rx.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleRxSelection(rx.id)}
                  />
                </DataCell>

                {/* Patient name */}
                <DataCell>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: table.cellFontSize, fontWeight: 500 }}
                  >
                    {rx.patient.lastName}, {rx.patient.firstName}
                  </Typography>
                </DataCell>

                {/* Rx number + date + therapies */}
                <DataCell>
                  <RxDetailsRow>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: table.cellFontSize, fontWeight: 600, minWidth: "70px" }}
                    >
                      {rx.rxNumber}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: table.cellFontSize,
                        color: "text.secondary",
                        minWidth: "80px",
                      }}
                    >
                      {rx.date
                        ? new Date(rx.date).toLocaleDateString("de-DE", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                        : "–"}
                    </Typography>
                    <span>
                      {rx.therapies.map((t, i) => (
                        <TherapyTag key={i}>{t.abbreviation}</TherapyTag>
                      ))}
                    </span>
                  </RxDetailsRow>
                </DataCell>

                {/* Validation indicator */}
                <DataCell align="left">
                  {SummarizedValidityIndicator(validationMap[rx.id])}
                </DataCell>

                {/* Taxieren stamp */}
                <DataCell align="left">
                  <Tooltip title="Sie müssen die Rechnung speichern, um das Rezept zu taxieren">
                    <span>
                      <RubberStamp text={rx.isTaxieren ? "taxiert" : ""} isdisabled />
                    </span>
                  </Tooltip>
                </DataCell>

                {/* Actions menu */}
                <DataCell align="center" sx={{ width: "48px" }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMenuOpen(e, rx);
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </DataCell>
              </DenseTableRow>
            );
          })}
        </TableBody>
      </DenseTable>
    </DenseTableContainer>
  );
};

export default React.memo(GkvBillTable);
